import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenMainzPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-mainz-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Mainz?",
      a: "In Mainz und dem Rhein-Main-Gebiet vergeben wir Termine meist innerhalb von 24-48 Stunden. Obwohl unser Hauptsitz in Rellingen bei Hamburg liegt, haben wir durch unser bundesweites Netzwerk auch im Rhein-Main-Gebiet erfahrene Gutachter vor Ort. Bei dringenden Fällen können wir oft noch schneller reagieren."
    },
    {
      q: "Wer trägt die Kosten für ein Unfallgutachten in Mainz?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie müssen nicht in Vorleistung gehen und haben das gesetzliche Recht auf freie Gutachterwahl. Lassen Sie sich nicht von der gegnerischen Versicherung zu deren Gutachter drängen – wir arbeiten unabhängig und ausschließlich in Ihrem Interesse."
    },
    {
      q: "Welche Gebiete in Mainz und Umgebung deckt ihr ab?",
      a: "Wir decken ganz Mainz ab – von der Altstadt bis Gonsenheim, von Bretzenheim bis Weisenau. Auch das gesamte Rhein-Main-Gebiet gehört zu unserem Einsatzgebiet: Wiesbaden, Frankfurt, Darmstadt, Ingelheim, Bingen, Bad Kreuznach und alle Gemeinden in Rheinhessen. Wir kommen dorthin, wo Ihr Fahrzeug steht."
    },
    {
      q: "Was ist bei einem Unfall auf der A60 oder A63 zu beachten?",
      a: "Unfälle auf den Autobahnen rund um Mainz – A60, A63, A643 (Schiersteiner Brücke) – kommen leider häufig vor. Nach einem Autobahnunfall dokumentieren Sie zunächst mit Fotos, bevor die Fahrzeuge bewegt werden. Wir kommen dann zur Werkstatt oder zum Abstellort und erstellen dort das Gutachten. Kontaktieren Sie uns möglichst früh, damit wir den Prozess beschleunigen können."
    },
    {
      q: "Macht ihr auch Gutachten in Wiesbaden und Frankfurt?",
      a: "Ja, selbstverständlich! Das gesamte Rhein-Main-Gebiet ist unser Einsatzgebiet. Neben Mainz betreuen wir auch Wiesbaden (15 Min. entfernt), Frankfurt (30 Min.), Darmstadt (40 Min.) und alle umliegenden Gemeinden. Für diese Städte haben wir auch eigene Landingpages mit lokalen Informationen."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Mainz | Kfz-Sachverständige Rhein-Main | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Mainz und Rheinhessen. Über 10 Jahre Erfahrung, gerichtsfeste Gutachten. Termine in 24-48h. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Mainz, Kfz Gutachter Mainz, Sachverständiger Mainz, Gutachter Rheinhessen, Unfallgutachter Rhein-Main, Kfz Gutachten Mainz" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-mainz" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Mainz & Rheinhessen",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Mainz, Rheinhessen und dem gesamten Rhein-Main-Gebiet.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Mainz",
              "addressRegion": "Rheinland-Pfalz",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Mainz"},
              {"@type": "City", "name": "Wiesbaden"},
              {"@type": "City", "name": "Ingelheim am Rhein"},
              {"@type": "City", "name": "Bingen am Rhein"},
              {"@type": "City", "name": "Bad Kreuznach"},
              {"@type": "City", "name": "Nieder-Olm"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Mainz & Rheinhessen</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Mainz</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Unabhängige Kfz-Sachverständige mit <strong className="text-white">über 10 Jahren Erfahrung</strong>. 
                  Wir erstellen gerichtsfeste Gutachten in der Landeshauptstadt und ganz Rheinhessen.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Ganz Rheinhessen</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termine in 24-48h</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Kostenlose Erstberatung</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Einsatzgebiet Mainz & Rheinhessen</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Abgedeckte Gebiete</p>
                      <p className="text-gray-400">Mainz, Rheinhessen, Rhein-Main-Gebiet</p>
                      <p className="text-gray-400">Wiesbaden, Frankfurt, Darmstadt, Ingelheim</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Terminvergabe</p>
                      <p className="text-gray-400">Meist innerhalb 24-48 Stunden</p>
                      <p className="text-gray-400">Bundesweites Gutachternetzwerk</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Hauptsitz Rellingen, bundesweit tätig</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter selbst wählen. Die gegnerische Versicherung muss die Kosten übernehmen – bundesweit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Mainz & Rheinhessen</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr unabhängiger Kfz-Sachverständiger für Mainz und das Rhein-Main-Gebiet. 
                Mit unserem Hauptsitz in Rellingen bei Hamburg und einem bundesweiten Netzwerk sind wir auch in der 
                <strong> Landeshauptstadt Rheinland-Pfalz</strong> schnell für Sie da.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte. 
                Die lebendige Stadt Mainz mit ihrer historischen Altstadt, dem Rheinufer und den vielen Pendlern 
                Richtung Frankfurt und Wiesbaden bedeutet leider auch viele Verkehrsunfälle. 
                Wir helfen Ihnen, alle Ihre Ansprüche <strong>gerichtsfest</strong> zu dokumentieren.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Unser Team arbeitet eng mit <strong>Fachanwälten für Verkehrsrecht</strong> in der Region zusammen. 
                Bei Bedarf können wir Sie direkt an einen spezialisierten Anwalt in Mainz oder Frankfurt weitervermitteln.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Verkehrsrechtsanwälten in Mainz und Frankfurt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Bundesweit:</strong> Wir sind in ganz Deutschland für Sie da</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Alle Ansprüche sichern – mehr als nur Reparaturkosten</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung nach Herstellervorgaben: Arbeitslohn, Original-Ersatzteile, Lackierung und alle Nebenkosten wie Achsvermessung.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Minderwert nach dem Unfall. Bei einem 3 Jahre alten Fahrzeug mit 3.000€ Schaden oft 300-600€ zusätzlich – auch nach perfekter Reparatur.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag ohne Ihr Fahrzeug: 23€ (Kleinwagen) bis 175€ (Oberklasse). Bei 10 Tagen schnell 350-500€ zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug auf dem lokalen Markt? Wir recherchieren aktuelle Angebote in der Region.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert des beschädigten Fahrzeugs bei Totalschaden. Wir holen mehrere Angebote ein für den bestmöglichen Preis.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos dokumentieren jeden Schaden. Diese Beweissicherung ist im Streitfall vor Gericht entscheidend.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Wichtig:</strong> Ein Werkstatt-Kostenvoranschlag enthält nur Reparaturkosten. 
                Wertminderung und Nutzungsausfall fehlen – das kann bei einem Golf oder Passat leicht 500-1.000€ sein!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Mainz</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Mainz & Rheinhessen</h2>
            <p className="text-lg text-gray-600 mb-8">Alle Mainzer Stadtteile und die Region Rheinhessen</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Mainz-Altstadt', 'Neustadt', 'Gonsenheim', 'Bretzenheim', 'Finthen', 'Mombach', 'Weisenau', 'Hechtsheim', 'Laubenheim', 'Ebersheim', 'Ingelheim', 'Bingen', 'Nieder-Olm', 'Nierstein', 'Oppenheim', 'Bad Kreuznach', 'Wiesbaden', 'Frankfurt'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Mainz beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Mainz & Rheinhessen • Termine in 24-48 Stunden</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-mainz" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenMainzPage;
